import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { useDecideCase } from "@/api/mutations/cases";
import { ApiError } from "@/api/client";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  entscheidungsMeldung,
  fassungLabel,
  letzteUeberarbeitung,
  ueberarbeitungAufgebraucht,
  ueberarbeitungSatz,
} from "./decisionLabel";
// #330 (UC-2): dieselbe Schablone wie an der Einrichtung — die Vorschau dort und die
// Anzeige hier müssen denselben Satz sagen, sonst liest sich eine Wahl anders als ihr Ergebnis.
import { entscheiderSatz, gefragtLabel } from "@/components/process-entscheider/entscheiderSatz";
import { CasefieldForm } from "@/components/casefield-form/CasefieldForm";
import {
  ausBeschreibungen,
  baueNutzlast,
  fehlendePflichtfelder,
  type CasefieldWerte,
} from "@/components/casefield-form/casefieldValues";
import type { PendingApproval } from "@/types";

// #303 (UC-2): die FREIGABE als Aufgabe — der erste Nutzer des `CasefieldForm`-Bausteins.
//
// Die Route existiert seit #302 (UC-1); in der Admin-Oberfläche gab es sie nicht. Ein Lauf
// stand auf „wartet auf Freigabe", und der einzige Weg weiter war Abbrechen. Deklariert der
// Mensch-Schritt `produces[]`, ist die Maske zugleich Dateneingabe (Konzept §4.3
// „Vorgangs-Formular") — eine Ablehnung liefert dagegen NIE Felder.

/**
 * #303 (Review M4): die SERVER-Meldung bei einer abgelehnten Eingabe durchreichen.
 *
 * Der Master nennt die Felder, an denen es hängt („these fields are still missing: …") —
 * ein pauschales „Pflichtfelder prüfen" warf genau die Auskunft weg, die weiterhilft. Der
 * Text ist Server-Text und wird von React escapt; ein fehlender/nicht-string Body fällt
 * auf den allgemeinen Satz zurück.
 */
function fehlerText(err: unknown): string {
  const status = err instanceof ApiError ? err.status : 0;
  const body = err instanceof ApiError ? (err.body as { error?: unknown; code?: unknown } | undefined) : undefined;
  if (status === 403) return "Sie tragen die entscheidende Rolle für diesen Schritt nicht.";
  if (status === 400) {
    const meldung = typeof body?.error === "string" ? body.error.trim() : "";
    // #324: der eine Fachfehler, den die Rohmeldung nicht erklärt. Er heisst nicht „Ihre
    // Eingabe passt nicht", sondern „dieser Schritt sieht das gar nicht vor" — z.B. weil
    // die Vorlage geändert wurde, während die Karte offen stand.
    if (body?.code === "REVISE_NOT_DECLARED") {
      return "Dieser Schritt sieht keine Überarbeitung vor — die Vorlage hat sich geändert. Bitte die Seite neu laden.";
    }
    return meldung
      ? `Die Eingaben passen nicht zur Vorlage: ${meldung}`
      : "Die Eingaben passen nicht zur Vorlage — Pflichtfelder und Feldtypen prüfen.";
  }
  if (status === 409) return "Der Vorgang wartet nicht mehr auf diese Freigabe.";
  return "Entscheidung konnte nicht gespeichert werden.";
}

export function ApprovalCard({ caseId, approval }: { caseId: string; approval: PendingApproval }) {
  const decide = useDecideCase();
  const snapshot = Object.entries(approval.snapshot ?? {});
  const felder = useMemo(() => {
    // #303 (Review M4): PFLICHT ist jedes produces-Feld, das im eingefrorenen Feldstand
    // noch nicht steht — nicht nur das in der Vorlage als `required` markierte. Der Master
    // verlangt bei der ABSCHLIESSENDEN Zustimmung ALLE produces; ein optional wirkendes
    // Feld, das nie gesetzt wurde, lief deshalb in eine 400, obwohl der Knopf frei war.
    // Bei Quorum 2 hat der Erste sie womöglich schon geliefert — dann stehen sie im
    // Snapshot und sind hier zu Recht nicht mehr Pflicht.
    const vorhanden = new Set([
      ...Object.keys(approval.snapshot ?? {}),
      // Ein GEKÜRZTER Snapshot trägt nur die Namen — die genügen für diese Aussage.
      ...(approval.snapshotKeys ?? []),
    ]);
    const pflicht = approval.produces.filter((k) => !vorhanden.has(k));
    // #303 (Review H1): die Beschreibungen kommen MIT der Freigabe. Der Entscheider ist
    // nicht der Inhaber der Einrichtung und darf deren Konfigurations-Sicht nicht lesen.
    return ausBeschreibungen(approval.produces, approval.feldDefs, { pflicht });
  }, [approval.produces, approval.feldDefs, approval.snapshot, approval.snapshotKeys]);
  const [werte, setWerte] = useState<CasefieldWerte>({});

  // #324 (UC-1): der dritte Ausgang. `null` UND ein fehlendes Feld heissen dasselbe — das
  // Gate erlaubt keine Überarbeitung, der Knopf erscheint nicht (default-closed; ein
  // `revise` liefe in `REVISE_NOT_DECLARED`).
  const ueberarbeitung = approval.ueberarbeitung ?? null;
  const [reviseOffen, setReviseOffen] = useState(false);
  const [feedback, setFeedback] = useState("");
  // Ein Auftrag aus Leerzeichen ist keiner — der Master lehnt ihn ab, aber ein gesperrter
  // Knopf sagt das, bevor jemand dagegen läuft (Muster: Rückfrage-Karte, #301 UC-5d).
  const feedbackLeer = feedback.trim() === "";
  const fassung = fassungLabel(approval.runde);
  // #324 (Review M1): der Deckel ist schon überschritten — ein weiteres `revise` führte
  // NICHT zurück, sondern hielte den Lauf an (Master: 200 mit `status:"blocked"`). Dieselbe
  // Begründung wie beim fehlenden `ueberarbeitung`-Feld: ein Knopf, der in einen stillen
  // Halt läuft, ist schlechter als ein gesperrter mit Grund.
  const aufgebraucht = ueberarbeitungAufgebraucht(approval.runde, ueberarbeitung?.maxRounds);
  const letzteRunde = letzteUeberarbeitung(approval.runde, ueberarbeitung?.maxRounds);
  // #324 (Review H1): der Lauf steht nach der eigenen Entscheidung. Bleibt sichtbar, bis die
  // Karte verschwindet — ein Ton, der nach vier Sekunden weg ist, wäre der einzige Hinweis
  // darauf gewesen, dass hier nichts weiterläuft.
  const [angehalten, setAngehalten] = useState(false);

  const fehlend = fehlendePflichtfelder(felder, werte);

  const entscheide = (decision: "approve" | "reject" | "revise") => {
    let fields: Record<string, string | number | boolean> | undefined;
    if (decision === "approve" && felder.length > 0) {
      try {
        fields = baueNutzlast(felder, werte);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Eingabe ungültig");
        return;
      }
    }
    // Die Pflicht gilt VOR dem Senden — nicht erst, wenn der Master mit 400 antwortet.
    if (decision === "revise" && feedbackLeer) return;
    decide.mutate(
      {
        id: caseId,
        stepId: approval.stepId,
        decision,
        fields,
        feedback: decision === "revise" ? feedback : undefined,
      },
      {
        onSuccess: (r) => {
          // ⚠ Die Meldung liest `status`, nicht nur `decision` (Review H1): der Master wirft
          // am Runden-Deckel NICHT, er antwortet 200 mit `status:"blocked"`. Ein
          // Erfolgs-Ton darauf schickte den Entscheider weg, während der Lauf steht.
          const { text, angehalten: steht } = entscheidungsMeldung(r);
          setAngehalten(steht);
          if (r.decision === "revise") {
            // Der Auftrag ist abgegeben — der Text darf weg. Das Feld bleibt aber offen,
            // wenn der Lauf steht: der Zustand muss sichtbar bleiben, nicht nur hörbar.
            setFeedback("");
            if (!steht) setReviseOffen(false);
          }
          if (steht) toast.warning(text, { duration: 10_000 });
          else toast.success(text);
        },
        onError: (err) => toast.error(fehlerText(err)),
      },
    );
  };

  return (
    <SectionCard
      title="Freigabe — der Vorgang wartet auf Ihre Entscheidung"
      description={`Schritt „${approval.stepName}" · entscheidet: Rolle ${approval.approverRole}`}
      icon={<ShieldCheck className="h-4 w-4 text-warn" />}
      className="border-warn-border"
      testId="case-pending-approval"
    >
      <div className="space-y-3">
        {/* #330 (UC-2): WER gerade gefragt ist — die aufgelöste Menge, nicht bloss die
            Rolle. Fehlen die Fakten (älterer Master, `null` = nicht ermittelbar), bleibt es
            byte-genau bei der Rollen-Angabe in der Überschrift: eine erfundene Menge wäre
            hier die schädlichste Auskunft, weil daraus jemand schliesst, ob er allein
            zuständig ist.
            ⚠ Anrede und Satz ziehen BEIDE aus `anzahl` (Fix-Runde 1, MED-9): hat eine
            gewählte Person die Rolle verlassen, stünde sonst „Gefragt ist: 2 gewählte
            Personen" da — die Schablone macht daraus „1 von 2 gewählten Personen". */}
        {approval.entscheider && (
          <p className="text-[12.5px] text-text2" data-testid="approval-entscheider">
            {gefragtLabel(approval.entscheider.anzahl)}: {entscheiderSatz(approval.entscheider)}
          </p>
        )}

        {/* #324 (UC-1): AB DER ZWEITEN Vorlage muss dranstehen, dass hier schon einmal
            entschieden wurde — sonst liest der Entscheider eine Überarbeitung wie eine
            Erstvorlage. Runde 1 (und eine nicht ermittelbare Runde) sagen nichts. */}
        {fassung && (
          <p className="text-[12.5px] font-semibold text-warn" data-testid="approval-runde">
            {fassung}
          </p>
        )}

        {approval.quorum > 1 && (
          <p className="text-[12.5px] text-text2" data-testid="approval-quorum">
            {/* `null` heisst „nicht ermittelbar", NICHT „0" — eine stille 0 löste beim
                Entscheider genau die Handlung aus, die er vielleicht schon getan hat. */}
            {approval.zustimmungen === null
              ? `Es müssen ${approval.quorum} Personen zustimmen; wie viele es bereits getan haben, ist gerade nicht ermittelbar.`
              : `${approval.zustimmungen} von ${approval.quorum} Zustimmungen liegen vor.`}
            {approval.entschiedenVon && approval.entschiedenVon.length > 0 && (
              <span className="ml-1 text-text3">
                Bereits entschieden: {approval.entschiedenVon.map((v) => v.replace(/^user:/, "")).join(", ")}
              </span>
            )}
          </p>
        )}

        {snapshot.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-text3">
              Entscheidungsgrundlage
            </div>
            <dl className="mt-1 grid gap-x-6 gap-y-1 sm:grid-cols-2" data-testid="approval-snapshot">
              {snapshot.map(([k, v]) => (
                <div key={k} className="min-w-0">
                  <dt className="font-mono text-[11px] text-text3">{k}</dt>
                  <dd className="break-words text-[12.5px] text-text">
                    {typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        {approval.snapshotKeys && approval.snapshotKeys.length > 0 && (
          <p className="text-[12px] text-warn" data-testid="approval-snapshot-gekuerzt">
            Der Feldstand war zu gross zum Mitschreiben — vorhanden sind:{" "}
            {approval.snapshotKeys.join(", ")}.
          </p>
        )}
        {approval.snapshot === null && !approval.snapshotKeys && (
          <p className="text-[12px] text-warn">
            Die Entscheidungsgrundlage ist nicht abrufbar — der Vorgang wartet trotzdem.
          </p>
        )}

        {felder.length > 0 && (
          <CasefieldForm
            felder={felder}
            werte={werte}
            onChange={setWerte}
            disabled={decide.isPending}
            testId="approval-form"
          />
        )}
        {fehlend.length > 0 && (
          <p className="text-[12px] text-warn" data-testid="approval-pflicht-fehlt">
            Zum Freigeben noch auszufüllen: {fehlend.join(", ")}
          </p>
        )}

        {/* #324 (UC-1): der Auftrag an den Ziel-Schritt. Er erscheint erst auf Klick — die
            Karte soll nicht mit drei gleichwertigen Feldern empfangen, und wer zurückgeben
            will, muss sagen WAS zu tun ist (der Master verlangt es, hier steht es vorher). */}
        {ueberarbeitung && reviseOffen && (
          <div className="rounded-md border border-warn-border bg-surface2 p-3" data-testid="approval-revise-panel">
            <p className="text-[12.5px] text-text2" data-testid="approval-revise-ziel">
              {ueberarbeitungSatz(ueberarbeitung)}
            </p>
            {/* #324 (Review M1): die letzte vorgesehene Runde ansagen, BEVOR sie verbraucht
                ist — danach hält der nächste Versuch den Lauf an. */}
            {letzteRunde && !angehalten && (
              <p className="mt-1 text-[12px] font-semibold text-warn" data-testid="approval-revise-letzte">
                Das ist die letzte vorgesehene Überarbeitung.
              </p>
            )}
            {/* #324 (Review H1): der Lauf steht — die Aussage bleibt stehen, auch wenn der
                Ton längst weg ist. */}
            {angehalten && (
              <p className="mt-1 text-[12px] font-semibold text-warn" data-testid="approval-revise-angehalten">
                Die Rückgabe ist verbucht, aber der Lauf ist angehalten und kommt nicht von
                selbst weiter — er braucht jetzt eine Freigabe, eine endgültige Ablehnung
                oder ein erneutes Anstossen.
              </p>
            )}
            <Textarea
              className="mt-2"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Was soll überarbeitet werden? Der Text geht als Auftrag in den Schritt zurück."
              disabled={decide.isPending}
              data-testid="approval-revise-feedback"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button
                onClick={() => entscheide("revise")}
                disabled={decide.isPending || feedbackLeer}
                data-testid="approval-revise-submit"
              >
                Zurückgeben
              </Button>
              <Button
                variant="ghost"
                onClick={() => setReviseOffen(false)}
                disabled={decide.isPending}
                data-testid="approval-revise-cancel"
              >
                Abbrechen
              </Button>
              {feedbackLeer && (
                <span className="text-[12px] text-warn" data-testid="approval-revise-pflicht">
                  Ohne Auftrag geht nichts zurück.
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            onClick={() => entscheide("approve")}
            disabled={decide.isPending || fehlend.length > 0}
            data-testid="approval-approve"
          >
            Freigeben
          </Button>
          {/* Ablehnen bleibt IMMER möglich: eine Ablehnung ist keine Datenabgabe, sie darf
              also nicht an einem leeren Pflichtfeld hängen. */}
          <Button
            variant="destructive"
            onClick={() => entscheide("reject")}
            disabled={decide.isPending}
            data-testid="approval-reject"
          >
            Ablehnen
          </Button>
          {/* Der dritte Ausgang — NUR wenn das Gate ihn deklariert. Wie das Ablehnen hängt
              er an keinem Pflichtfeld: eine Rückgabe ist keine Datenabgabe. */}
          {ueberarbeitung && (
            <Button
              variant="outline"
              onClick={() => setReviseOffen(true)}
              disabled={decide.isPending || reviseOffen || aufgebraucht}
              data-testid="approval-revise"
            >
              Zur Überarbeitung
            </Button>
          )}
          {/* #324 (Review M1): ein gesperrter Knopf ohne Grund sieht aus wie eine Störung. */}
          {ueberarbeitung && aufgebraucht && (
            <span className="text-[12px] text-warn" data-testid="approval-revise-erschoepft">
              Die vorgesehenen Überarbeitungen sind aufgebraucht — jetzt geht nur noch
              freigeben oder ablehnen.
            </span>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
